import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Phone, Send, Loader2, Instagram } from "lucide-react";
import { Link } from "react-router-dom";
import { FadeUp, MaskedLines, Overline } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUBJECTS = [
  "Voluntariat RISE 27'",
  "Parteneriat",
  "Informații Generale",
  "Altele",
];

const inputCls =
  "w-full rounded-xl border border-white/10 bg-carddark px-4 py-3.5 text-sm text-neutral-100 placeholder:text-neutral-500 transition-colors duration-300 focus:border-neon focus:outline-none focus:ring-1 focus:ring-neon";

const INFO = [
  {
    icon: Mail,
    label: "Email",
    value: "contact@artandoutdoor.com",
    href: "mailto:contact@artandoutdoor.com",
    testid: "contact-info-email",
  },
  {
    icon: MapPin,
    label: "Sediu",
    value: "Str. Ștefan Luchian nr. 3, ap. 10, Alba Iulia, jud. Alba",
    testid: "contact-info-location",
  },
  {
    icon: Phone,
    label: "Telefon",
    value: "0762 971 209",
    href: "tel:+40762971209",
    testid: "contact-info-phone",
  },
  {
    icon: Instagram,
    label: "Instagram",
    value: "@artandoutdoor.ro",
    href: "https://www.instagram.com/artandoutdoor.ro/",
    testid: "contact-info-instagram",
  },
];

const BANK = [
  { label: "Denumire", value: "Asociația Art & Outdoor România", testid: "contact-bank-name" },
  { label: "CIF", value: "38948024", testid: "contact-bank-cif" },
  { label: "IBAN", value: "RO33 RNCB 0003 1859 9467 0001", testid: "contact-bank-iban" },
  { label: "Bancă", value: "Banca Comercială Română (BCR)", testid: "contact-bank-bank" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: SUBJECTS[0], message: "" });
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Mesajul a fost trimis! Îți răspundem în cel mai scurt timp.");
      setForm({ name: "", email: "", subject: SUBJECTS[0], message: "" });
    } catch (err) {
      toast.error("Ceva nu a mers bine. Te rugăm să încerci din nou.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-12 lg:py-28">
      <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Overline>Contact</Overline>
          <h1 className="mt-4 font-display text-4xl font-black uppercase leading-[1.02] tracking-tight text-white sm:text-5xl lg:text-6xl">
            <MaskedLines lines={[<>Hai să</>, <span key="v" className="text-neon">vorbim.</span>]} />
          </h1>
          <FadeUp delay={0.5}>
            <p className="mt-8 max-w-md text-base leading-relaxed text-neutral-400 sm:text-lg">
              Vrei să fii voluntar la RISE 27', să devii partener sau pur și
              simplu să afli mai multe despre proiectele noastre? Scrie-ne —
              citim fiecare mesaj.
            </p>
          </FadeUp>
          <div className="mt-12 space-y-6">
            {INFO.map(({ icon: Icon, label, value, href, testid }, i) => (
              <FadeUp key={testid} delay={0.6 + i * 0.1}>
                <div data-testid={testid} className="flex items-center gap-4">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-surface text-neon">
                    <Icon className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">{label}</p>
                    {href ? (
                      <a
                        href={href}
                        className="mt-1 block text-sm font-bold text-white transition-colors hover:text-neon"
                      >
                        {value}
                      </a>
                    ) : (
                      <p className="mt-1 text-sm font-bold text-white">{value}</p>
                    )}
                  </div>
                </div>
              </FadeUp>
            ))}
            <FadeUp delay={0.95}>
              <div
                data-testid="contact-bank-card"
                className="rounded-3xl border border-white/10 bg-surface p-6"
              >
                <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-neon">
                  Cont bancar · donații
                </p>
                <div className="mt-4 space-y-3">
                  {BANK.map(({ label, value, testid }) => (
                    <div key={testid}>
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                        {label}
                      </p>
                      <p data-testid={testid} className="mt-0.5 text-sm font-bold text-white">
                        {value}
                      </p>
                    </div>
                  ))}
                </div>
                <Link
                  to="/redirectioneaza-3-5"
                  data-testid="contact-redirect-link"
                  className="mt-5 inline-flex rounded-full border border-white/15 px-5 py-2.5 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-neutral-300 transition-colors hover:border-neon hover:text-neon"
                >
                  Redirecționează 3,5% din impozit
                </Link>
              </div>
            </FadeUp>
            <FadeUp delay={1}>
              <p
                data-testid="contact-legal-name"
                className="border-t border-white/10 pt-6 font-mono text-[10px] uppercase leading-relaxed tracking-[0.2em] text-neutral-500"
              >
                Asociația Art &amp; Outdoor România · Organizație non-profit
              </p>
            </FadeUp>
          </div>
        </div>

        <FadeUp delay={0.3} className="lg:col-span-7">
          <form
            data-testid="contact-form"
            onSubmit={handleSubmit}
            className="rounded-3xl border border-white/10 bg-surface p-6 sm:p-10"
          >
            <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-flame">
              Trimite-ne un mesaj
            </p>
            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="contact-name" className="mb-2 block font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400">
                  Nume Complet
                </label>
                <input
                  id="contact-name"
                  data-testid="contact-input-name"
                  type="text"
                  required
                  minLength={2}
                  maxLength={100}
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Numele tău"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="mb-2 block font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400">
                  Adresă de Email
                </label>
                <input
                  id="contact-email"
                  data-testid="contact-input-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder="nume@email.ro"
                  className={inputCls}
                />
              </div>
            </div>
            <div className="mt-6">
              <label htmlFor="contact-subject" className="mb-2 block font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400">
                Subiect
              </label>
              <select
                id="contact-subject"
                data-testid="contact-select-subject"
                value={form.subject}
                onChange={set("subject")}
                className={`${inputCls} appearance-none`}
              >
                {SUBJECTS.map((s) => (
                  <option key={s} value={s} className="bg-carddark">
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="mt-6">
              <label htmlFor="contact-message" className="mb-2 block font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400">
                Mesaj
              </label>
              <textarea
                id="contact-message"
                data-testid="contact-input-message"
                required
                minLength={10}
                maxLength={2000}
                rows={6}
                value={form.message}
                onChange={set("message")}
                placeholder="Spune-ne cu ce te putem ajuta..."
                className={`${inputCls} resize-none`}
              />
            </div>
            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={loading}
              className="group mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-neon px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-canvas transition-colors duration-300 hover:bg-white disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Se trimite...
                </>
              ) : (
                <>
                  Trimite Mesajul
                  <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </>
              )}
            </button>
          </form>
        </FadeUp>
      </div>
    </div>
  );
}
